// SUB-OFFICE 7 — FORM 8-R, JUNCTION RELIEF DUTY. The rotunda trunk vomits
// canisters faster than dignity allows; you throw levers by glyph before the
// window closes. The arrival schedule and the correct lever are pure functions
// of the seed (arrivals8R / window8R / expectedLevers8R) — the client renders
// FROM them and records ONLY raw {n, lever, t}; the server recomputes every
// timeout, misroute, and band. Glyph/correctness are NEVER client-asserted.

import { arrivals8R, window8R, type Form8RSpec } from '../../shared/physspec';
import { printLayer } from '../ui/chrome';
import { PhysLogRecorder } from './log';
import type { BenchDeps } from './bench';
import type { BehaviorLog } from '../../shared/physlog';

export function runJunction(
  deps: BenchDeps,
  taskId: string,
  spec: Form8RSpec
): Promise<BehaviorLog | null> {
  return new Promise((resolve) => {
    const rec = new PhysLogRecorder('FORM8R', taskId);
    const layer = printLayer();
    const arrivals = arrivals8R(spec);

    const card = document.createElement('div');
    card.className = 'paper-modal wide';
    card.innerHTML =
      `<div class="paper-head letterhead"><span>FORM 8-R — JUNCTION RELIEF DUTY</span>` +
      `<span class="tab-num" id="jx-count">0 / ${spec.count}</span></div>`;
    const body = document.createElement('div');
    body.className = 'paper-body typewriter';
    card.appendChild(body);
    layer.appendChild(card);

    // ---- shift bulletin: forced haste + grade-5 out-of-service remap --------
    const bulletin = document.createElement('div');
    bulletin.className = 'compose-note';
    const outGlyph = spec.out_of_service !== null ? spec.lever_glyphs[spec.out_of_service] : null;
    const remapGlyph = spec.remap_to !== null ? spec.lever_glyphs[spec.remap_to] : null;
    const lines: string[] = ['ROUTE EACH CANISTER TO THE LEVER BEARING ITS GLYPH.'];
    if (spec.forced_haste) lines.push('HASTE AUTHORIZED. DIGNITY WAIVED. ONE MISROUTE FORGIVEN.');
    if (outGlyph && remapGlyph) lines.push(`SHIFT BULLETIN: ${outGlyph} IS OUT OF SERVICE. ROUTE ITS TRAFFIC TO ${remapGlyph}.`);
    bulletin.textContent = lines.join(' ');
    body.appendChild(bulletin);

    // ---- the arriving canister (glyph chit) --------------------------------
    const stage = document.createElement('div');
    stage.style.cssText = 'margin:12px 0;min-height:88px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;border:1px solid var(--carbon);background:var(--paper)';
    body.appendChild(stage);
    const chit = document.createElement('div');
    chit.className = 'stamp stamp-ink letterhead';
    chit.style.cssText = 'font-size:22px;padding:8px 18px';
    chit.textContent = '— WAITING —';
    stage.appendChild(chit);
    const timerBar = document.createElement('div');
    timerBar.style.cssText = 'height:6px;width:70%;background:rgba(28,26,23,.15);position:relative;overflow:hidden';
    const timerFill = document.createElement('div');
    timerFill.style.cssText = 'position:absolute;inset:0 100% 0 0;background:var(--carbon)';
    timerBar.appendChild(timerFill);
    stage.appendChild(timerBar);

    // ---- the lever bank ----------------------------------------------------
    const bank = document.createElement('div');
    bank.style.cssText = 'display:flex;flex-wrap:wrap;gap:10px;justify-content:center;margin-top:6px';
    body.appendChild(bank);

    const status = document.createElement('div');
    status.className = 'compose-note tab-num';
    status.style.marginTop = '10px';
    body.appendChild(status);

    const foot = document.createElement('div');
    foot.className = 'paper-foot';
    const abandonBtn = document.createElement('button');
    abandonBtn.type = 'button';
    abandonBtn.className = 'slam-btn secondary';
    abandonBtn.textContent = 'ABANDON THE JUNCTION';
    foot.appendChild(abandonBtn);
    card.appendChild(foot);

    let handled = 0;
    let shreds = 0;
    let over = false;
    let raf = 0;
    // FIFO of arrived, unhandled canister indices
    const queue: number[] = [];
    let nextArrival = 0;
    const arrivedAt = new Map<number, number>(); // n -> ms (rec-time) it arrived

    const countEl = card.querySelector('#jx-count') as HTMLElement;

    const routeFront = (lever: number): void => {
      if (!queue.length) return;
      const n = queue.shift()!;
      const arr = arrivedAt.get(n) ?? rec.now();
      rec.ev('lever', { n, lever });
      rec.ev('latency_probe', { ms: Math.max(1, rec.now() - arr) });
      deps.bus.emit('audio:thunk');
      handled++;
      updateChit();
    };

    // build the lever buttons
    spec.lever_glyphs.forEach((glyph, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      const oos = spec.out_of_service === i;
      b.className = 'slam-btn' + (oos ? ' secondary' : '');
      b.style.cssText = 'font-size:12px;min-width:96px' + (oos ? ';text-decoration:line-through;opacity:.7' : '');
      b.textContent = oos ? `${glyph} · OUT` : glyph;
      b.addEventListener('click', () => {
        if (over) return;
        routeFront(i);
      });
      bank.appendChild(b);
    });

    const updateChit = (): void => {
      countEl.textContent = `${handled} / ${spec.count}`;
      status.textContent = `ROUTED ${handled} · SHRED ${shreds} · WAITING ${queue.length}`;
      if (queue.length) {
        const n = queue[0];
        chit.textContent = spec.lever_glyphs[spec.dest[n]];
        chit.classList.toggle('stamp-red', spec.kindness_idxs.includes(n));
      } else {
        chit.textContent = handled + shreds >= spec.count ? '— CLEARED —' : '— WAITING —';
        chit.classList.remove('stamp-red');
        timerFill.style.right = '100%';
      }
    };

    const finish = (ok: boolean): void => {
      if (over) return;
      over = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey);
      if (ok) rec.ev('shift_complete', {});
      else rec.ev('abandoned', { remaining: spec.count - (handled + shreds) });
      card.remove();
      if (!ok) {
        resolve(null);
        return;
      }
      const built = rec.build(false);
      if (built.valid) resolve(built.log);
      else {
        deps.bus.emit('toast', `The junction log did not assemble (${built.reason}). The duty remains open.`);
        resolve(null);
      }
    };

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') finish(false);
      // number keys throw the matching lever (keyboard play)
      const k = parseInt(e.key, 10);
      if (!isNaN(k) && k >= 1 && k <= spec.lever_glyphs.length) routeFront(k - 1);
    };
    window.addEventListener('keydown', onKey);
    abandonBtn.addEventListener('click', () => finish(false));

    rec.ev('mounted', { levers: spec.lever_count });
    const frame = (): void => {
      if (over) return;
      raf = requestAnimationFrame(frame);
      const now = rec.now();
      // arrivals
      while (nextArrival < spec.count && arrivals[nextArrival] <= now) {
        const n = nextArrival;
        rec.ev('canister_arrived', { n });
        queue.push(n);
        arrivedAt.set(n, now);
        nextArrival++;
        deps.bus.emit('audio:flap');
        if (queue.length === 1) updateChit();
      }
      // front timeout (theater — server recomputes)
      if (queue.length) {
        const n = queue[0];
        const arr = arrivedAt.get(n) ?? now;
        const win = window8R(spec, n);
        const remain = arr + win - now;
        timerFill.style.right = `${Math.max(0, Math.min(100, (remain / win) * 100)).toFixed(1)}%`;
        if (remain <= 0) {
          rec.ev('timeout_shred', { n });
          deps.bus.emit('audio:buzz');
          queue.shift();
          shreds++;
          updateChit();
        }
      }
      if (handled + shreds >= spec.count && nextArrival >= spec.count) {
        updateChit();
        finish(true);
        return;
      }
    };
    raf = requestAnimationFrame(frame);
  });
}
